import { useLayoutEffect, useRef, type RefObject } from "react";

const SLIDE_DURATION_MS = 350;

const prefersNoMotion = () =>
  document.documentElement.dataset.flyuiSkipAnimations === "true" ||
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Slides `slideRef` from where it was to where it now is when `trigger` changes and its layout position moved,
 * so content above it can take its final size at once while this element glides into place. Other layout shifts,
 * such as the page settling on load, don't slide. `anchorRef` is an untransformed ancestor used to measure the
 * position, so an in-flight slide doesn't skew the next one.
 */
export const useSlideOnMove = (
  anchorRef: RefObject<HTMLElement | null>,
  slideRef: RefObject<HTMLElement | null>,
  trigger: number
) => {
  const previousTop = useRef<number | null>(null);
  const previousTrigger = useRef<number | null>(null);

  // Runs every render: it only measures, and animates when the trigger changed and the position moved.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const slide = slideRef.current;
    if (!anchor || !slide) {
      previousTop.current = null;
      return;
    }

    const top = anchor.getBoundingClientRect().top + window.scrollY + parseFloat(getComputedStyle(anchor).paddingTop);
    const previous = previousTop.current;
    const triggerChanged = previousTrigger.current !== null && previousTrigger.current !== trigger;
    previousTop.current = top;
    previousTrigger.current = trigger;

    if (previous === null || previous === top || !triggerChanged || prefersNoMotion()) {
      return;
    }

    slide.animate([{ translate: `0 ${previous - top}px` }, { translate: "0 0" }], {
      duration: SLIDE_DURATION_MS,
      easing: "cubic-bezier(0.25, 0, 0.1, 1)",
    });
  });
};
